import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { Organization, Patient } from "src/shared/types";
import { apiGet } from "src/utils/api";

interface OrganizationProviderProps {
  organization: Organization;
  patientId?: string | null;
  children: ReactNode;
  patientData: Patient | null;
  loadingPatientData: boolean;
}

export interface ConnectionStatus {
  connectionStatus: boolean;
  isLoadingConnectionStatus: boolean;
}

export type OrganizationContextType = Omit<OrganizationProviderProps, 'children'>;
export const OrganizationContext = createContext<OrganizationContextType | null>(null);

export const OrganizationProvider = ({ organization, patientId, children }: OrganizationProviderProps) => {
  const [ patientData, setPatientData ] = useState<Patient | null>(null);
  const [ loadingPatientData, setLoadingPatientData ] = useState(false);

  const fetchPatientInfo = useCallback(async () => {
    try {
      if (patientId) {
        setLoadingPatientData(true);
        const response = await apiGet(`/api/${organization.id}/patients/${patientId}/patient`);
        const data = await response.json();
        if (!data.error) {
          setPatientData(data);
        }
      }
    } catch (_) {
      setPatientData(null);
    } finally {
      setLoadingPatientData(false);
    }
  }, [ organization, patientId ]);

  useEffect(() => {
    fetchPatientInfo();
  }, [ fetchPatientInfo ]);

  return (
    <OrganizationContext.Provider value={{
      organization,
      patientId,
      patientData,
      loadingPatientData,
    }}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganizationContext = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (context === undefined) throw new Error('useOrganization must be used within an OrganizationProvider');
  return context as OrganizationContextType;
};

export const useCurrentOrganization = (): Organization => {
  const { organization } = useOrganizationContext();
  useEffect(() => {
    if (!organization) {
      window.location.href = '/error';
    }
  }, [organization]);

  return organization;
};
